// Four duck-mascot SVG variants ported from the prototype's components-shared.jsx.
// Themed via CSS vars so swapping themes works without prop drilling.

import type { MascotName } from '../theme/ThemeProvider';

type Props = {
  variant?: MascotName;
  size?: number;
  className?: string;
};

type SubProps = { size: number; className?: string | undefined };

function DuckClassic({ size, className }: SubProps) {
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="dc-shadow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(0,0,0,0.25)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>
      </defs>
      <ellipse cx="100" cy="178" rx="62" ry="8" fill="url(#dc-shadow)" />
      <ellipse cx="100" cy="130" rx="68" ry="42" fill="rgb(var(--color-duck))" />
      <path d="M 32 120 Q 18 110 26 134 Q 38 138 42 128 Z" fill="rgb(var(--color-duck))" />
      <circle cx="138" cy="78" r="38" fill="rgb(var(--color-duck))" />
      <path d="M 168 76 Q 192 80 168 92 Q 162 88 168 76 Z" fill="rgb(var(--color-beak))" />
      <circle cx="146" cy="70" r="6" fill="rgb(var(--color-ink))" />
      <circle cx="148" cy="68" r="2" fill="#fff" />
      <ellipse cx="130" cy="92" rx="6" ry="3" fill="rgb(var(--color-beak))" opacity="0.4" />
    </svg>
  );
}

function DuckPixel({ size, className }: SubProps) {
  const px = 10;
  const grid = [
    '00000000111100000000',
    '00000001111111000000',
    '00000111111111100000',
    '00001111111311100000',
    '00001111113331100000',
    '00001111111311100000',
    '00011111111112220000',
    '00011111111122000000',
    '00111111111100000000',
    '01111111111110000000',
    '11111111111111000000',
    '11111122111111100000',
    '11112211211111110000',
    '11122111111111110000',
    '11111111111111110000',
    '01111111111111100000',
    '00111111111111000000',
    '00001111111100000000',
    '00000111110000000000',
    '00000000000000000000',
  ];
  const cells: JSX.Element[] = [];
  for (let y = 0; y < grid.length; y++) {
    const row = grid[y];
    if (row === undefined) continue;
    for (let x = 0; x < row.length; x++) {
      const c = row[x];
      if (c === '0' || c === undefined) continue;
      const fill = c === '1' ? 'rgb(var(--color-duck))' : c === '2' ? 'rgb(var(--color-beak))' : 'rgb(var(--color-ink))';
      cells.push(<rect key={`${x},${y}`} x={x * px} y={y * px} width={px} height={px} fill={fill} />);
    }
  }
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      style={{ display: 'block', shapeRendering: 'crispEdges' }}
      aria-hidden="true"
    >
      {cells}
    </svg>
  );
}

function DuckGeo({ size, className }: SubProps) {
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      <circle cx="92" cy="130" r="56" fill="rgb(var(--color-duck))" />
      <circle cx="140" cy="78" r="34" fill="rgb(var(--color-duck))" />
      <polygon points="170,72 196,82 170,90" fill="rgb(var(--color-beak))" />
      <circle cx="146" cy="70" r="7" fill="rgb(var(--color-ink))" />
      <path d="M 60 130 A 32 32 0 0 1 124 130 Z" fill="rgb(var(--color-beak))" opacity="0.25" />
      <rect x="78" y="180" width="10" height="10" fill="rgb(var(--color-beak))" />
      <rect x="100" y="180" width="10" height="10" fill="rgb(var(--color-beak))" />
    </svg>
  );
}

function DuckOutline({ size, className }: SubProps) {
  const stroke = 'rgb(var(--color-ink))';
  const sw = 4;
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      <ellipse cx="100" cy="130" rx="68" ry="42" fill="rgb(var(--color-duck))" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
      <circle cx="138" cy="78" r="36" fill="rgb(var(--color-duck))" stroke={stroke} strokeWidth={sw} />
      <path d="M 168 74 Q 196 82 168 92 Q 162 88 168 74 Z" fill="rgb(var(--color-beak))" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
      <circle cx="146" cy="70" r="5" fill={stroke} />
      <path d="M 78 122 Q 100 100 132 120" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
      <path d="M 32 122 Q 18 112 28 134" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />
      <path d="M 70 152 Q 90 144 110 152" fill="none" stroke={stroke} strokeWidth={sw - 1} strokeLinecap="round" opacity="0.6" />
    </svg>
  );
}

const MASCOTS = {
  classic: DuckClassic,
  pixel: DuckPixel,
  geo: DuckGeo,
  outline: DuckOutline,
} as const;

export function Duck({ variant = 'classic', size = 200, className }: Props): JSX.Element {
  const Comp = MASCOTS[variant];
  return <Comp size={size} className={className} />;
}
